import { DOC_TYPE } from '@components/imageHandling/common';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';

export const useGetImagesByPriorities = () => {
  const selectImagesByPriority = (images: ImageDocumentStoreItem[], maxImages = 3) => {
    const priorityMap = {
      'Setup Sheet 1': 1,
      '2D Drawing 1': 2,
      'Clamping Image 1': 3,
      'Part Image 1': 4,
      'Clamping Image 2': 5,
      'Clamping Image 3': 6,
      'Setup Sheet 2': 7,
      '2D Drawing 2': 8,
      'Setup Sheet 3': 9,
      '2D Drawing 3': 10,
      'Part Image 2': 11,
      'Part Image 3': 12,
    } as const;

    const uniqueImages = new Map();
    const allUniqueImages = new Map();
    const typeGroups: Record<string, ImageDocumentStoreItem[]> = {
      [DOC_TYPE.SETUP_SHEET]: [],
      [DOC_TYPE['2D_DRAWING']]: [],
      'Clamping Image': [],
      'Part Image': [],
    };

    images.forEach((image) => {
      if (allUniqueImages.has(image.contentHash)) {
        return;
      }
      allUniqueImages.set(image.contentHash, image);

      if (uniqueImages.has(image.contentHash)) {
        return;
      }

      const type = image?.documentTag;
      if (type && typeGroups[type]) {
        uniqueImages.set(image.contentHash, image);
        typeGroups[type].push(image);
      }
    });

    const candidates: { image: ImageDocumentStoreItem; priority: number; type: keyof typeof typeGroups; number: number }[] = [];

    Object.keys(typeGroups).forEach((type) => {
      typeGroups[type].forEach((image, index) => {
        const number = index + 1;
        const priorityKey = `${type} ${number}` as keyof typeof priorityMap;
        const priority = priorityMap[priorityKey];

        if (priority !== undefined) {
          candidates.push({
            image,
            priority,
            type,
            number,
          });
        }
      });
    });

    candidates.sort((a, b) => a.priority - b.priority);

    const selectedCandidates = candidates.slice(0, maxImages);

    if (selectedCandidates.length < maxImages && allUniqueImages.size >= maxImages) {
      const selectedHashes = new Set(selectedCandidates.map((candidate) => candidate.image.contentHash));
      const remainingImages = Array.from(allUniqueImages.values()).filter((image) => !selectedHashes.has(image.contentHash));

      const additionalCount = maxImages - selectedCandidates.length;
      const additionalImages = remainingImages.slice(0, additionalCount);

      additionalImages.forEach((image) => {
        const type = image?.documentTag || image?.documentType;
        selectedCandidates.push({
          image,
          priority: 999,
          type,
          number: 0,
        });
      });
    }

    return selectedCandidates.map((candidate) => ({
      type: candidate.type,
      image: candidate.image,
    }));
  };

  return { selectImagesByPriority };
};
